// Per-element HUD visibility. Each toggleable overlay registers its
// DOM element under a stable key; visibility persists in localStorage
// and updates live when the settings panel flips a checkbox.
//
// Why a runtime registry instead of just CSS classes: HUD elements
// have inline `display: none/block/flex` set by their own logic
// (e.g., legendary pill is hidden until you pick one up). We need a
// second-axis "user wants this hidden" flag that doesn't collide with
// the gameplay-driven flag.

export type HudKey =
  | 'playersList'
  | 'killFeed'
  | 'quest'
  | 'legendary'
  | 'wallet'
  | 'themeBadge'
  | 'connBadge'
  | 'lowHpVignette'
  | 'roomLabel';

const STORAGE_PREFIX = 'smp.hud.';

export const HUD_TOGGLES: ReadonlyArray<{ key: HudKey; label: string; defaultOn: boolean; hint?: string }> = [
  { key: 'playersList',   label: 'Players list',          defaultOn: true,  hint: 'Top-left scoreboard' },
  { key: 'killFeed',      label: 'Kill feed',             defaultOn: true,  hint: 'Right-side eliminations' },
  { key: 'quest',         label: 'Boss quest tracker',    defaultOn: true,  hint: 'Right-side venue progress' },
  { key: 'legendary',     label: 'Legendary weapon pill', defaultOn: true,  hint: 'Bottom-left equipped slot' },
  { key: 'wallet',        label: 'Wallet (💎 ✨)',         defaultOn: true,  hint: 'Bottom-right currency' },
  { key: 'themeBadge',    label: 'Theme badge',           defaultOn: true,  hint: 'Bottom-left [T] hint' },
  { key: 'connBadge',     label: 'Connection badge',      defaultOn: true,  hint: 'Bottom-center disconnect alert' },
  { key: 'lowHpVignette', label: 'Low-HP red vignette',   defaultOn: true,  hint: 'Pulses when HP < 30%' },
  { key: 'roomLabel',     label: 'Room identity pill',    defaultOn: true,  hint: 'Bottom-left current server id' },
];

export function isHudVisible(key: HudKey): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + key);
    if (raw === null) {
      const def = HUD_TOGGLES.find((t) => t.key === key);
      return def ? def.defaultOn : true;
    }
    return raw === '1';
  } catch { return true; }
}

export function setHudVisible(key: HudKey, on: boolean): void {
  try { localStorage.setItem(STORAGE_PREFIX + key, on ? '1' : '0'); } catch { /* */ }
  dispatchEvent(new CustomEvent<HudToggleDetail>('smp:hud-toggle', { detail: { key, on } }));
}

export type HudToggleDetail = { key: HudKey; on: boolean };

// Bind an element to a HUD key. Adds a CSS class that wins over inline
// `display: block/flex` set by gameplay logic (legendary pill, quest
// tracker, etc.) so the user's hide preference always sticks.
export function bindHud(el: HTMLElement, key: HudKey): void {
  ensureCss();
  el.dataset.hudKey = key;
  applyHudVisibility(el, isHudVisible(key));
  addEventListener('smp:hud-toggle', (ev) => {
    const e = ev as CustomEvent<HudToggleDetail>;
    if (e.detail.key === key) applyHudVisibility(el, e.detail.on);
  });
}

function applyHudVisibility(el: HTMLElement, on: boolean): void {
  el.classList.toggle('smp-hud-hidden', !on);
}

let cssInstalled = false;
function ensureCss(): void {
  if (cssInstalled) return;
  cssInstalled = true;
  const s = document.createElement('style');
  s.textContent = `.smp-hud-hidden { display: none !important; }`;
  document.head.appendChild(s);
}
